/*
 * off | tutors | all — the staged-rollout gate, shared.
 *
 * This logic already shipped and is already live: more-like-this-panel.jsx has
 * run tutors-only in production on REACT_APP_MORE_LIKE_THIS_AUDIENCE. It was a
 * private function in that file, so the next feature needing a staged rollout
 * had to either import from a panel component or copy it.
 *
 * HONEST SCOPE, because the first version of this comment overclaimed. There are
 * SIX independent implementations of this same off|tutors|all parse in the repo:
 * this one, audio-listen-panel/audio-audience.js, TWO different
 * gl-shared/gl-audience.js + gl-why-capture/gl-audience.js (already-realized
 * drift — same exported names, same env vars, separate files),
 * gl-why-capture/gl-tag-guard.js, and an inline snippet in public/index.html.
 * This change folds in exactly ONE of them, so the count is unchanged. It does
 * not "prevent drift" yet; it gives the drift somewhere to converge. The GL ones
 * additionally support a `cohort` audience this helper does not, so absorbing
 * them needs a wider contract and is deliberately not attempted here.
 *
 * TAKES THE VALUE, NOT THE VAR NAME, and that is deliberate. CRA's DefinePlugin
 * only substitutes STATIC `process.env.FOO` member accesses; a dynamic
 * `process.env[name]` survives only because the whole env object also happens to
 * be emitted, which is a build-tool detail no gate should depend on. Every call
 * site therefore writes `process.env.REACT_APP_X_AUDIENCE` in full, which is
 * also greppable — you can find every gated feature with one search.
 *
 * WHAT THIS IS NOT: access control. It decides what the UI RENDERS, from a role
 * in localStorage that the browser owns. A student can set that value, and the
 * API stays reachable regardless. For a staged rollout — show a new surface to
 * tutors, collect feedback, widen it — that is the right tool. For anything
 * that must not be REACHABLE, gate it on the server.
 */

/** The role read, in one place. Passed BY REFERENCE so it stays lazy. */
const readRole = () =>
  typeof window !== "undefined" ? window.localStorage.getItem("role") : null;

export const AUDIENCE_ALL = "all";
export const AUDIENCE_TUTORS = "tutors";
export const AUDIENCE_OFF = "off";

/**
 * Pure decision, so it can be tested without a DOM or a build.
 *
 * @param {string|undefined|null} envValue raw REACT_APP_*_AUDIENCE value
 * @param {string|null} role               caller's role ("ROLE_TUTOR", ...)
 * @param {string} whenAbsent              what an unset var means — see below
 */
export const audienceAllowsValue = (envValue, role, whenAbsent) => {
  const raw = envValue === undefined || envValue === null ? whenAbsent : envValue;
  const value = String(raw).trim().toLowerCase();

  if (value === AUDIENCE_ALL) return true;
  if (value === AUDIENCE_TUTORS) {
    // LAZILY, and that is a fix rather than a style choice. The inline version
    // this replaced read localStorage only inside THIS branch; reading it up
    // front made "all"/"off"/absent touch storage where they previously did not
    // — and in a context where storage THROWS (blocked cookies, a sandboxed
    // iframe), a panel that used to render started throwing inside render.
    // Measured by a reviewer; jsdom's localStorage never throws, so no test
    // could have caught it. Same role key as src/pages/mentorships/.
    const resolved = typeof role === "function" ? role() : role;
    return resolved === "ROLE_TUTOR" || resolved === "ROLE_ADMIN";
  }
  // "off" AND anything unrecognized — including a blank-bound "" from a
  // buildspec that declared the var and left it empty. Never silently open: a
  // typo'd audience must HIDE the feature, not reveal it.
  return false;
};

/**
 * `whenAbsent` IS REQUIRED — it deliberately has no default, and that is the
 * single most important line in this file.
 *
 * It used to default to "all". Both call sites pass it explicitly, so NOTHING
 * pinned that default: a reviewer flipped it and all 216 suites stayed green.
 * Which means the default could never help and could only ever hurt — the one
 * thing it could do is let a future third caller write
 * `audienceAllows(process.env.REACT_APP_NEW_AUDIENCE)`, omit the second
 * argument, and silently ship a staged feature to every student. A permissive
 * default in a module whose thesis is "fail closed" is a trap with no upside.
 *
 * Required means the compiler cannot catch it but the reader must choose, and
 * the two existing callers choose OPPOSITELY for good reasons:
 *
 *   MLT passes "all" — it was ALREADY LIVE for everyone when the audience var
 *   was introduced, so an absent var has to mean "behave exactly as before".
 *
 *   Power Search passes "off" — it is dark, so there is no prior behaviour to
 *   preserve, and the failure to protect against is shipping the master flag
 *   while forgetting the audience var. Under an "all" default that mistake
 *   opens a beta feature to every student silently; under "off" it shows it to
 *   nobody and someone notices.
 */
export const audienceAllows = (envValue, whenAbsent) =>
  audienceAllowsValue(envValue, readRole, whenAbsent);
